import React from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Check, Users, Clock, Briefcase, ArrowRight } from 'lucide-react';
import useScrollAnimation from '../hooks/useScrollAnimation';

const ScrollReveal = ({ children, delay = 0, className = '' }) => {
  const [ref, isVisible] = useScrollAnimation(0.1);
  return (
    <div 
      ref={ref} 
      className={`transition-all duration-1000 ${className} ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

const PricingPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero Section - Dark Cinematic */}
      <section className="relative min-h-[50vh] flex items-center overflow-hidden bg-[#1a1a1a]">
        <div className="absolute inset-0">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1920&h=1080&fit=crop)' }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1a1a1a] via-[#1a1a1a]/90 to-[#1a1a1a]/60" />
        </div>
        <div className="relative z-10 max-w-6xl mx-auto px-6 pt-24">
          <div className="flex items-center space-x-4 mb-6">
            <div className="w-12 h-[1px] bg-white/50"></div>
            <span className="text-white/50 text-sm tracking-widest uppercase">Pricing</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
            Engagement <span className="text-[#8D6E63]">Models</span>
          </h1>
          <p className="text-xl text-white/70 max-w-2xl font-light">
            Choose the engagement model that fits your business needs. Flexible pricing with no hidden costs.
          </p>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <ScrollReveal delay={0}>
              <PricingCard 
                icon={<Users className="w-10 h-10" />}
                title="Full Time Employee"
                subtitle="On Our Payroll"
                description="Dedicated team member working exclusively for your business, managed and paid by us."
                features={[
                  'Interview & select your resource',
                  'Full-time availability (9 AM - 11:30 PM IST)',
                  'Direct reporting to you',
                  'All tools & software included',
                  'Hassle-free replacement policy',
                  'US holidays & leave aligned',
                  'Commerce graduates / ACCA staff',
                  'Save up to 35% on costs'
                ]}
              />
            </ScrollReveal>

            <ScrollReveal delay={150}>
              <PricingCard 
                icon={<Clock className="w-10 h-10" />}
                title="Time & Material"
                subtitle="T&M Model"
                description="Flexible hourly model perfect for variable workloads. Pay only for the time you use."
                features={[
                  'No lock-in period',
                  'Pay only for hours used',
                  'Start immediately',
                  'Weekly detailed timesheets',
                  'Task-level time tracking',
                  'No hour expiration',
                  'On-demand expertise',
                  'Save up to 40% on costs'
                ]}
              />
            </ScrollReveal>

            <ScrollReveal delay={300}>
              <PricingCard 
                icon={<Briefcase className="w-10 h-10" />}
                title="Project-Based"
                subtitle="Fixed Scope Delivery"
                description="Fixed-fee arrangement for well-defined deliverables with clear milestones."
                features={[
                  'Fixed-fee pricing upfront',
                  'Clearly defined scope & milestones',
                  'Flexible for additional work',
                  'Pre-agreed hourly rate for extras',
                  'Minimal onboarding required',
                  'Budget visibility & control',
                  'Milestone-based delivery',
                  'Ideal for one-time projects'
                ]}
              />
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-5xl mx-auto px-6">
          <ScrollReveal>
            <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-4">
              Compare Engagement Models
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
              Find the perfect fit for your business requirements
            </p>
          </ScrollReveal>
          
          <ScrollReveal delay={200}>
            <div className="overflow-x-auto">
              <table className="w-full bg-white shadow-xl rounded-2xl overflow-hidden">
                <thead className="bg-gradient-to-r from-[#3E2723] to-[#4E342E] text-white">
                  <tr>
                    <th className="py-5 px-6 text-left font-semibold">Feature</th>
                    <th className="py-5 px-6 text-center font-semibold">FTE Model</th>
                    <th className="py-5 px-6 text-center font-semibold">T&M Model</th>
                    <th className="py-5 px-6 text-center font-semibold">Project-Based</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  <TableRow feature="Commitment" fte="Long-term" hourly="None" project="Project duration" />
                  <TableRow feature="Pricing" fte="Monthly fixed" hourly="Per hour" project="Fixed scope" />
                  <TableRow feature="Resource" fte="100% dedicated" hourly="Shared pool" project="Project-focused" />
                  <TableRow feature="Flexibility" fte="High" hourly="Very High" project="Medium" />
                  <TableRow feature="Cost Savings" fte="Up to 35%" hourly="Up to 40%" project="Varies" />
                  <TableRow feature="Best For" fte="Ongoing needs" hourly="Variable work" project="One-time tasks" />
                </tbody>
              </table>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* CTA - Simple */}
      <section className="relative py-12 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#2d2420] via-[#3E2723] to-[#2d2420]"></div>
        
        <ScrollReveal className="relative z-10 max-w-6xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between">
          <div className="text-center md:text-left mb-6 md:mb-0">
            <h2 className="text-2xl md:text-3xl font-bold text-white">
              Ready to Get Started?
            </h2>
            <p className="text-white/60 mt-1">Let's discuss the best engagement model for you</p>
          </div>
          <a href="mailto:hello@trueledger.com?subject=Schedule%20a%20Consultation&body=Hi%20Trueledger%20Team,%0A%0AI%20would%20like%20to%20schedule%20a%20consultation%20to%20discuss%20pricing%20and%20engagement%20options.%0A%0AThank%20you.">
            <Button className="bg-white text-[#3E2723] hover:bg-[#8D6E63] hover:text-white px-8 py-5 text-base shadow-lg transition-all group">
              Schedule a Consultation
              <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </a>
        </ScrollReveal>
      </section>

      <Footer />
    </div>
  );
};

const PricingCard = ({ icon, title, subtitle, description, features, highlighted }) => (
  <div className={`bg-white border border-stone-200 rounded-lg overflow-hidden shadow-sm hover:shadow-lg hover:border-[#3E2723] transition-all h-full flex flex-col ${highlighted ? 'ring-2 ring-[#8D6E63]' : ''}`}>
    {/* Header - Aligned */}
    <div className="bg-stone-50 p-6 text-center h-[160px] flex flex-col items-center justify-center border-b border-stone-100 flex-shrink-0">
      <div className="text-[#3E2723] mb-3">
        {icon}
      </div>
      <h3 className="text-xl font-bold text-gray-900">
        {title}
      </h3>
      <p className="text-[#8D6E63] text-sm mt-1">
        {subtitle}
      </p>
    </div>
    
    {/* Body */}
    <div className="p-6 flex flex-col flex-grow">
      <p className="text-gray-600 mb-6 text-center text-sm">
        {description}
      </p>
      <ul className="space-y-3 flex-grow">
        {features.map((feature, index) => (
          <li key={index} className="flex items-start">
            <Check className="w-4 h-4 text-[#3E2723] flex-shrink-0 mt-0.5 mr-2" />
            <span className="text-gray-700 text-sm">{feature}</span>
          </li>
        ))}
      </ul>
      <a href="mailto:hello@trueledger.com?subject=Get%20Started%20with%20Trueledger&body=Hi%20Trueledger%20Team,%0A%0AI%20am%20interested%20in%20getting%20started%20with%20your%20services.%0A%0AThank%20you." className="mt-6">
        <Button className="w-full py-3 bg-white hover:bg-[#3E2723] hover:text-white text-[#3E2723] border border-[#3E2723] transition-colors">
          Get Started
        </Button>
      </a>
    </div>
  </div>
);

const TableRow = ({ feature, fte, hourly, project }) => (
  <tr className="hover:bg-stone-50 transition-colors">
    <td className="py-4 px-6 font-semibold text-gray-900">{feature}</td>
    <td className="py-4 px-6 text-center text-gray-600">{fte}</td>
    <td className="py-4 px-6 text-center text-gray-600 bg-[#8D6E63]/5">{hourly}</td>
    <td className="py-4 px-6 text-center text-gray-600">{project}</td>
  </tr>
);

export default PricingPage;
